import { Box, Icon, Typography } from "@outschool/backpack";
import { faComment } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { BlockScopedMessageInput } from "@outschool/ui-components-classroom";
import React from "react";

import { useMessageTeacherDrawerContext } from "./MessageTeacherDrawerContext";
import ThreadViewLayout from "./ThreadViewLayout";
import { PostTarget } from "./types";
import { buildLearnerSender, getPostTarget } from "./utils";

type NewThreadViewProps = {
  postTitle?: string;
  hideBackButton?: boolean;
  onBack: () => void;
  onClose: () => void;
  onThreadCreated: (threadUid: string) => void;
} & PostTarget;

export default function NewThreadView(props: NewThreadViewProps) {
  const { section, learner, teacherName } = useMessageTeacherDrawerContext();
  const { onBack, onClose, onThreadCreated } = props;
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\NewThreadView",
  );
  const postTarget = getPostTarget(props);
  const isGeneral =
    !props.classPostUid && !props.learningAssignmentClassPostUid;
  const normalizedPostTitle = props.postTitle?.trim() ?? "";
  const hasPostTitle = normalizedPostTitle.length > 0;

  const title = isGeneral
    ? t("Ask {{teacherName}} anything", { teacherName })
    : hasPostTitle
    ? normalizedPostTitle
    : t("Message {{teacherName}}", { teacherName });

  const subtitle = isGeneral
    ? undefined
    : hasPostTitle
    ? t("Ask your teacher a question about this post.")
    : t("Ask anything about the course");

  return (
    <ThreadViewLayout
      postTitle={title}
      postSubtitle={subtitle}
      showBackButton={!props.hideBackButton}
      onBack={onBack}
      onClose={onClose}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 24,
          gap: 5,
        }}
      >
        <Icon
          icon={faComment}
          sx={{
            color: "neutral.400",
            fontSize: "4rem",
            mb: 4,
          }}
        />
        <Typography variant="h6" color="text.secondary">
          {t("Start a conversation")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          {t("Type your question below")}
        </Typography>
      </Box>

      <BlockScopedMessageInput
        placeholder={t("Message your teacher")}
        sectionUid={section.uid}
        learnerUid={learner.uid}
        {...postTarget}
        sender={buildLearnerSender(learner)}
        onThreadCreated={onThreadCreated}
        sx={{ borderColor: "divider" }}
      />
    </ThreadViewLayout>
  );
}
